import type { FloatRangeInput } from '#src/__generated__/globalTypes.ts';
import { GameplayTier } from '#src/__generated__/globalTypes.ts';

import { clamp } from './math.ts';

export const WHOLE_STEP_GAMEPLAY_TIERS = [
  GameplayTier.GOAT,
  GameplayTier.STAR,
  GameplayTier.IMPACT,
  GameplayTier.ROSTER,
  GameplayTier.DNP,
];

export const GAMEPLAY_TIER_DISPLAY_ORDER: GameplayTier[] = [
  GameplayTier.GOAT,
  GameplayTier.STAR_PLUS,
  GameplayTier.STAR,
  GameplayTier.IMPACT_PLUS,
  GameplayTier.IMPACT,
  GameplayTier.ROSTER_PLUS,
  GameplayTier.ROSTER,
  GameplayTier.DNP_PLUS,
  GameplayTier.DNP,
];

export const isGameplayTier = (value: string): value is GameplayTier => {
  return GAMEPLAY_TIER_DISPLAY_ORDER.includes(value as GameplayTier);
};

const sortByGameplayTier = (a: string, b: string) => {
  if (!isGameplayTier(a)) {
    return 1;
  }
  if (!isGameplayTier(b)) {
    return -1;
  }
  return (
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(a) -
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(b)
  );
};

export { sortByGameplayTier };

// Whole-star fallback, half-steps round down. Prefer
// useGameplayTierRatings().starsOf, which renders half-steps exactly.
export const gameplayTierStarCount = (gameplayTier: GameplayTier) =>
  ({
    [GameplayTier.GOAT]: 5,
    [GameplayTier.STAR_PLUS]: 4,
    [GameplayTier.STAR]: 4,
    [GameplayTier.IMPACT_PLUS]: 3,
    [GameplayTier.IMPACT]: 3,
    [GameplayTier.ROSTER_PLUS]: 2,
    [GameplayTier.ROSTER]: 2,
    [GameplayTier.DNP_PLUS]: 1,
    [GameplayTier.DNP]: 1,
  })[gameplayTier];

export const GAMEPLAY_TIER_LABELS: Record<GameplayTier, string> = {
  [GameplayTier.GOAT]: 'Icon',
  [GameplayTier.STAR_PLUS]: 'Star+',
  [GameplayTier.STAR]: 'Star',
  [GameplayTier.IMPACT_PLUS]: 'Key+',
  [GameplayTier.IMPACT]: 'Key',
  [GameplayTier.ROSTER_PLUS]: 'Roster+',
  [GameplayTier.ROSTER]: 'Roster',
  [GameplayTier.DNP_PLUS]: 'Reserve+',
  [GameplayTier.DNP]: 'Reserve',
};

export const NBA_GAMEPLAY_TIER_MIN_L10_SCORES: Record<GameplayTier, number> = {
  [GameplayTier.GOAT]: 50,
  [GameplayTier.STAR_PLUS]: 45,
  [GameplayTier.STAR]: 40,
  [GameplayTier.IMPACT_PLUS]: 35,
  [GameplayTier.IMPACT]: 30,
  [GameplayTier.ROSTER_PLUS]: 25,
  [GameplayTier.ROSTER]: 20,
  [GameplayTier.DNP_PLUS]: 15,
  [GameplayTier.DNP]: 0,
};

const NBA_TOP_GAMEPLAY_TIER_MAX_L10_SCORE = 75;

const tierRanksAway = (gameplayTier: GameplayTier, ranksUp: number) =>
  GAMEPLAY_TIER_DISPLAY_ORDER[
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(gameplayTier) - ranksUp
  ] as GameplayTier | undefined;

export const nextGameplayTier = (gameplayTier: GameplayTier) =>
  tierRanksAway(gameplayTier, 1);

export const previousGameplayTier = (gameplayTier: GameplayTier) =>
  tierRanksAway(gameplayTier, -1);

/**
 * @returns the share, from 0 to 1, of the way from the tier's minimum L10 to the next tier's.
 */
export const nbaGameplayTierProgress = (
  gameplayTier: GameplayTier,
  l10Score: number
) => {
  const nextTier = nextGameplayTier(gameplayTier);
  const tierMin = NBA_GAMEPLAY_TIER_MIN_L10_SCORES[gameplayTier];
  const tierMax = nextTier
    ? NBA_GAMEPLAY_TIER_MIN_L10_SCORES[nextTier]
    : NBA_TOP_GAMEPLAY_TIER_MAX_L10_SCORE;

  return clamp((l10Score - tierMin) / (tierMax - tierMin), 0, 1);
};

// Mirrors the backend star cap.
const UNTIERED_PLAYER_STAR_COST = 1;

export const nbaGameplayTierStarCost = (gameplayTierStars: Nullable<number>) =>
  gameplayTierStars ?? UNTIERED_PLAYER_STAR_COST;

export const isWithinGameplayTierStars = (
  gameplayTierStars: Nullable<number>,
  range: Nullable<FloatRangeInput>
) => {
  const { min, max } = range ?? {};
  const stars = nbaGameplayTierStarCost(gameplayTierStars);

  return stars >= (min ?? -Infinity) && stars <= (max ?? Infinity);
};

export const capStarsRange = (
  gameplayTierStars: Nullable<FloatRangeInput>,
  maxAffordableStars: number | undefined
): Nullable<FloatRangeInput> => {
  if (maxAffordableStars === undefined) {
    return gameplayTierStars;
  }

  return {
    ...gameplayTierStars,
    max: Math.min(gameplayTierStars?.max ?? Infinity, maxAffordableStars),
  };
};
